import { API_BASE_URL, pushAPI, clubSettingsAPI } from './api';
import type { PushUnavailableReason } from '../types/push';

const SERVICE_WORKER_URL = '/sw.js';

/** A böngésző támogatja-e a Web Push-t (service worker + Push API + Notification API). */
export const isPushSupported = (): boolean =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

export const isIos = (): boolean =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  // iPadOS 13+ asztali user agentet küld, de van érintőképernyője
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

/** Kezdőképernyőre telepített (PWA) módban fut-e az app. */
export const isStandalone = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (window.navigator as any).standalone === true;

export const getPermission = (): NotificationPermission =>
  'Notification' in window ? Notification.permission : 'denied';

/**
 * iOS/iPadOS csak akkor engedi a Web Push-t, ha az appot a felhasználó
 * hozzáadta a kezdőképernyőhöz. Böngészőfülön a PushManager sem érhető el.
 */
export const requiresHomeScreenInstall = (): boolean => isIos() && !isStandalone();

/**
 * A service worker regisztrálása. Hívható többször is - a böngésző
 * felismeri a meglévő regisztrációt.
 */
export const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!('serviceWorker' in navigator)) {
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register(SERVICE_WORKER_URL, { scope: '/' });
    // Megvárjuk, hogy aktív legyen, különben a pushManager hívások elhasalhatnak
    await navigator.serviceWorker.ready;
    return registration;
  } catch (error) {
    console.error('Service worker regisztráció sikertelen:', error);
    return null;
  }
};

/**
 * Elküldi a service workernek az API címet, a JWT tokent és a VAPID kulcsot.
 * Erre azért van szükség, mert a service worker nem éri el a localStorage-ot,
 * viszont önállóan kell tudnia újra feliratkozni (pushsubscriptionchange).
 */
export const syncServiceWorkerConfig = async (vapidPublicKey?: string | null): Promise<void> => {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const token = localStorage.getItem('token');
  if (!token) {
    return;
  }

  const registration = await navigator.serviceWorker.ready;
  const worker = registration.active ?? navigator.serviceWorker.controller;

  // A klub feltöltött címere lesz az értesítések ikonja. Ha nem érhető el,
  // a service worker a beépített logóra esik vissza.
  //
  // Nem a nyers feltöltött fájlt adjuk át, hanem a méretre igazított
  // ikon-végpontot: az mindig négyzetes, és töredék akkora, mint egy pár
  // megabájtos eredeti címer - ez minden egyes értesítésnél letöltődik.
  let clubIconUrl: string | null = null;
  let clubBadgeUrl: string | null = null;
  try {
    const settings = await clubSettingsAPI.getSettings();
    if (settings.crestUrl) {
      clubIconUrl = `${API_BASE_URL}/club-settings/icon/192.png`;
      // Az Android a badge-et monokróm sziluetté maszkolja az értesítési
      // sávban, ahhoz ez a kisebb méret is bőven elég.
      clubBadgeUrl = `${API_BASE_URL}/club-settings/icon/96.png`;
    }
  } catch {
    // az ikon nem kritikus
  }

  worker?.postMessage({
    type: 'SET_PUSH_CONFIG',
    config: {
      apiBaseUrl: API_BASE_URL,
      token,
      vapidPublicKey: vapidPublicKey ?? null,
      clubIconUrl,
      clubBadgeUrl,
    },
  });
};

/** Kijelentkezéskor töröljük a service workerben tárolt tokent. */
export const clearServiceWorkerConfig = async (): Promise<void> => {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const registration = await navigator.serviceWorker.getRegistration();
  const worker = registration?.active ?? navigator.serviceWorker.controller;
  worker?.postMessage({ type: 'CLEAR_PUSH_CONFIG' });
};

/** Az eszköz aktuális push előfizetése, ha van. */
export const getCurrentSubscription = async (): Promise<PushSubscription | null> => {
  if (!isPushSupported()) {
    return null;
  }

  const registration = await navigator.serviceWorker.getRegistration();
  return (await registration?.pushManager.getSubscription()) ?? null;
};

/**
 * Megvizsgálja, hogy ezen az eszközön egyáltalán bekapcsolható-e a push.
 * Null = bekapcsolható.
 */
export const getUnavailableReason = async (): Promise<PushUnavailableReason | null> => {
  if (requiresHomeScreenInstall()) {
    return 'ios-needs-install';
  }

  if (!isPushSupported()) {
    return 'unsupported';
  }

  const { enabled } = await pushAPI.getVapidPublicKey();
  if (!enabled) {
    return 'server-disabled';
  }

  if (getPermission() === 'denied') {
    return 'permission-denied';
  }

  return null;
};

/**
 * Feliratkozás push értesítésekre ezen az eszközön.
 * FONTOS: felhasználói interakcióból (gombnyomás) kell hívni, különben
 * a böngésző nem jeleníti meg az engedélykérő ablakot.
 */
export const subscribeToPush = async (): Promise<PushSubscription> => {
  if (requiresHomeScreenInstall()) {
    throw new Error(
      'iPhone/iPad esetén előbb add hozzá az appot a kezdőképernyőhöz (Megosztás → Főképernyőhöz adás), utána kapcsolható be az értesítés.'
    );
  }

  if (!isPushSupported()) {
    throw new Error('Ez a böngésző nem támogatja a push értesítéseket.');
  }

  const { publicKey, enabled } = await pushAPI.getVapidPublicKey();
  if (!enabled || !publicKey) {
    throw new Error('A push értesítések nincsenek beállítva a szerveren.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error(
      permission === 'denied'
        ? 'Az értesítések le vannak tiltva. A böngésző oldalbeállításainál engedélyezheted újra.'
        : 'Az értesítési engedély nem lett megadva.'
    );
  }

  const registration = (await navigator.serviceWorker.getRegistration()) ?? (await registerServiceWorker());
  if (!registration) {
    throw new Error('A háttérszolgáltatás (service worker) nem indult el.');
  }

  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      // A böngészők csak látható értesítést engedélyeznek, néma adatküldést nem
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }));

  await pushAPI.subscribe(toSubscribePayload(subscription));
  await syncServiceWorkerConfig(publicKey);

  return subscription;
};

/** Leiratkozás ezen az eszközön (a többi eszköz feliratkozása megmarad). */
export const unsubscribeFromPush = async (): Promise<void> => {
  const subscription = await getCurrentSubscription();
  if (!subscription) {
    return;
  }

  const endpoint = subscription.endpoint;

  try {
    await subscription.unsubscribe();
  } finally {
    await pushAPI.unsubscribe(endpoint);
  }
};

/**
 * Bejelentkezés után: ha a felhasználó korábban már engedélyezte az értesítéseket,
 * csendben - engedélykérő ablak nélkül - visszaállítjuk a feliratkozást, és a
 * jelenlegi felhasználóhoz kötjük. Kijelentkezéskor ugyanis leiratkozunk, hogy
 * közös eszközön ne kapja meg az előző felhasználó értesítéseit.
 */
export const ensureSubscribed = async (): Promise<boolean> => {
  if (!isPushSupported() || requiresHomeScreenInstall() || getPermission() !== 'granted') {
    return false;
  }

  try {
    const { publicKey, enabled } = await pushAPI.getVapidPublicKey();
    if (!enabled || !publicKey) {
      return false;
    }

    const registration = (await navigator.serviceWorker.getRegistration()) ?? (await registerServiceWorker());
    if (!registration) {
      return false;
    }

    const subscription =
      (await registration.pushManager.getSubscription()) ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      }));

    await pushAPI.subscribe(toSubscribePayload(subscription));
    await syncServiceWorkerConfig(publicKey);
    return true;
  } catch (error) {
    console.error('A push előfizetés visszaállítása sikertelen:', error);
    return false;
  }
};

const toSubscribePayload = (subscription: PushSubscription) => {
  const json = subscription.toJSON();

  if (!json.keys?.p256dh || !json.keys?.auth) {
    throw new Error('A böngésző hiányos push előfizetést adott vissza.');
  }

  return {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
    },
  };
};

/** A base64url VAPID kulcs átalakítása a PushManager által várt bájttömbbé. */
const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);

  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }

  return output;
};
